// src/pages/ColisManager.jsx
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import {
    collection,
    addDoc,
    serverTimestamp,
    query,
    orderBy,
    onSnapshot,
    doc,
    updateDoc,
    getDocs,
    getDoc,
    writeBatch,
    deleteDoc,
} from "firebase/firestore";

import UploadField from "../components/UploadField";
import ArticlesEditor from "../components/ArticlesEditor";
import PaymentsEditor from "../components/PaymentsEditor";
import LotsEditor from "../components/LotsEditor";
import ProgressChecklist from "../components/ProgressChecklist";

import {
    genTrackingCode,
    computeCoverage,
    computePaymentStatus,
    deriveStatusFromCoverage,
    checkCoverageConsistency,
} from "../utils/tracking";
function TrashIcon({ size = 14 }) {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size}
            viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            aria-hidden="true">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            <path d="M10 11v6" /><path d="M14 11v6" />
            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
        </svg>
    );
}

export default function ColisManager() {
    // ------- Formulaire Création -------
    const [code, setCode] = useState(genTrackingCode());
    const [clientName, setClientName] = useState("");
    const [clientPhone, setClientPhone] = useState("");
    const [origin, setOrigin] = useState("Chine");
    const [destination, setDestination] = useState("Lomé");
    const [estimate, setEstimate] = useState("");
    const [cover, setCover] = useState("");
    const [articles, setArticles] = useState([]);
    const [payments, setPayments] = useState([]);
    const [lots, setLots] = useState([]);
    const [saving, setSaving] = useState(false);

    // ------- Liste des colis existants -------
    const [list, setList] = useState([]);

    // ------- Actions rapides (modales) -------
    const [activeColis, setActiveColis] = useState(null);
    const [showPay, setShowPay] = useState(false);
    const [showLot, setShowLot] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const emptyPayment = {
        amount: "",
        currency: "XOF",
        rate: 1,
        method: "cash",
        note: "",
        date: new Date().toISOString(),
    };
    const [payForm, setPayForm] = useState(emptyPayment);

    const emptyLot = {
        step: "warehouse",
        qty: 1,
        note: "",
        date: new Date().toISOString(),
        images: [],
    };
    const [lotForm, setLotForm] = useState(emptyLot);

    // ------- Abonnement temps réel -------
    useEffect(() => {
        const q = query(collection(db, "colischine"), orderBy("createdAt", "desc"));
        const unsub = onSnapshot(q, (snap) => {
            const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setList(arr);
        });
        return () => unsub();
    }, []);

    // ------- Agrégats du formulaire d'ajout -------
    const paidTotalXOF = useMemo(
        () => payments.reduce((s, p) => s + Number(p.amount || 0) * Number(p.rate || 1), 0),
        [payments]
    );

    const articlesTotalXOF = useMemo(
        () =>
            articles.reduce((s, it) => {
                const unit = it.unitValueXOF ?? it.value ?? 0;
                return s + Number(it.qty || 0) * Number(unit || 0);
            }, 0),
        [articles]
    );

    const balance = useMemo(
        () => Number(estimate || 0) - Number(paidTotalXOF || 0),
        [estimate, paidTotalXOF]
    );

    const coverage = useMemo(() => computeCoverage(articles, lots), [articles, lots]);
    const coverageIssues = useMemo(() => checkCoverageConsistency(coverage), [coverage]);
    const paymentStatus = useMemo(
        () => computePaymentStatus(estimate, paidTotalXOF),
        [estimate, paidTotalXOF]
    );
    const status = useMemo(() => deriveStatusFromCoverage(coverage), [coverage]);

    // ------- Helpers back-end (recalcule & MAJ doc colis) -------
    const recomputePaymentAggregates = async (colisId) => {
        // 1) total payé en XOF
        const paysSnap = await getDocs(collection(db, "colischine", colisId, "payments"));
        let totalXOF = 0;
        paysSnap.forEach((d) => {
            const p = d.data();
            totalXOF += Number(p.amount || 0) * Number(p.rate || 1);
        });

        // 2) récupérer estimation actuelle
        const cRef = doc(db, "colischine", colisId);
        const cSnap = await getDoc(cRef);
        const est = Number(cSnap.data()?.estimateXOF || 0);

        // 3) MAJ agrégats
        await updateDoc(cRef, {
            paidTotalXOF: totalXOF,
            balanceXOF: est - totalXOF,
            updatedAt: serverTimestamp(),
        });
    };

    // Remplace ta fonction si tu l'as
    const recomputeStatusAggregates = async (colisId) => {
        // Articles & lots
        const artSnap = await getDocs(collection(db, "colischine", colisId, "articles"));
        const lotsSnap = await getDocs(collection(db, "colischine", colisId, "lots"));
        const arts = artSnap.docs.map((d) => d.data());
        const lts = lotsSnap.docs.map((d) => d.data());

        // Couverture & statut proposé
        const cov = computeCoverage(arts, lts);
        let nextStatus = deriveStatusFromCoverage(cov);

        // Paiement courant
        const cRef = doc(db, "colischine", colisId);
        const cSnap = await getDoc(cRef);
        const cur = cSnap.data() || {};
        const est = Number(cur.estimateXOF || 0);
        const paid = Number(cur.paidTotalXOF || 0);

        // ⚠️ Si la couverture dirait "recu" mais que ce n'est pas soldé, on NE PASSE PAS à "recu"
        if (nextStatus === "recu" && paid < est) {
            nextStatus = cur.status || "en_conteneur"; // garde le statut courant
        }

        await updateDoc(cRef, { status: nextStatus, updatedAt: serverTimestamp() });
    };
    // Supprime une sous-collection par lots (batch) pour éviter >500 ops
    async function deleteSubcollection(colisId, subName) {
        const subRef = collection(db, "colischine", colisId, subName);
        const snap = await getDocs(subRef);
        if (snap.empty) return;

        const docs = snap.docs;
        for (let i = 0; i < docs.length; i += 400) {
            const chunk = docs.slice(i, i + 400);
            const batch = writeBatch(db);
            chunk.forEach((d) => batch.delete(d.ref));
            await batch.commit();
        }
    }

    async function deleteColis(colis) {
        const ok = window.confirm(
            `Supprimer définitivement le colis ${colis.code} ?\nCette action est irréversible.`
        );
        if (!ok) return;

        try {
            setDeletingId(colis.id);

            // 1) Effacer sous-collections
            await Promise.all([
                deleteSubcollection(colis.id, "articles"),
                deleteSubcollection(colis.id, "payments"),
                deleteSubcollection(colis.id, "lots"),
            ]);

            // 2) Effacer le document principal
            await deleteDoc(doc(db, "colischine", colis.id));

            alert("Colis supprimé.");
        } catch (e) {
            console.error(e);
            alert("Erreur lors de la suppression du colis.");
        } finally {
            setDeletingId(null);
        }
    }


    // ------- CRUD principal -------
    const resetForm = () => {
        setCode(genTrackingCode());
        setClientName("");
        setClientPhone("");
        setOrigin("Chine");
        setDestination("Lomé");
        setEstimate("");
        setCover("");
        setArticles([]);
        setPayments([]);
        setLots([]);
    };

    const save = async () => {
        try {
            setSaving(true);
            const payload = {
                code,
                clientName,
                clientPhone,
                origin,
                destination,
                estimateXOF: Number(estimate || 0),
                paidTotalXOF: Number(paidTotalXOF || 0),
                balanceXOF: Number(balance || 0),
                status, // en_attente | en_entrepot | en_conteneur | recu
                cover: cover || null,
                articlesTotalXOF: Number(articlesTotalXOF || 0),
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            };

            const ref = await addDoc(collection(db, "colischine"), payload);

            const promises = [];
            if (Array.isArray(articles) && articles.length) {
                for (const it of articles) {
                    promises.push(addDoc(collection(db, "colischine", ref.id, "articles"), it));
                }
            }
            if (Array.isArray(payments) && payments.length) {
                for (const p of payments) {
                    promises.push(addDoc(collection(db, "colischine", ref.id, "payments"), p));
                }
            }
            if (Array.isArray(lots) && lots.length) {
                for (const l of lots) {
                    promises.push(addDoc(collection(db, "colischine", ref.id, "lots"), l));
                }
            }
            await Promise.all(promises);

            resetForm();
            alert("Colis enregistré avec succès.");
        } catch (e) {
            console.error(e);
            alert("Erreur lors de l'enregistrement");
        } finally {
            setSaving(false);
        }
    };

    // Remplace ta fonction actuelle
    const markReceived = async (id) => {
        const cRef = doc(db, "colischine", id);
        const cSnap = await getDoc(cRef);
        const data = cSnap.data();
        const est = Number(data?.estimateXOF || 0);
        const paid = Number(data?.paidTotalXOF || 0);

        if (paid < est) {
            alert("Impossible de marquer 'Reçu' : le colis n'est pas totalement payé.");
            return;
        }

        await updateDoc(cRef, {
            status: "recu",
            updatedAt: serverTimestamp(),
        });
    };

    // ------- Actions rapides : ouvrir modales -------
    const openPayModal = (colis) => {
        setActiveColis(colis);
        setPayForm({ ...emptyPayment });
        setShowPay(true);
    };

    const openLotModal = (colis) => {
        setActiveColis(colis);
        setLotForm({ ...emptyLot });
        setShowLot(true);
    };

    // ------- Soumission modales -------
    const submitPayment = async () => {
        if (!activeColis?.id) return;
        const path = collection(db, "colischine", activeColis.id, "payments");
        await addDoc(path, payForm);
        await recomputePaymentAggregates(activeColis.id);
        setShowPay(false);
    };

    const submitLot = async () => {
        if (!activeColis?.id) return;
        if (lotForm.step === "container" && !(lotForm.containerNo || lotForm.shippingUrl)) {
            alert("Renseigne au moins le N° de conteneur ou le lien du site de shipping.");
            return;
        }
        await addDoc(collection(db, "colischine", activeColis.id, "lots"), lotForm);
        await recomputeStatusAggregates(activeColis.id);
        setShowLot(false);
    };

    const [expandedId, setExpandedId] = useState(null);
    const [details, setDetails] = useState({});      // { [colisId]: { articles:[], lots:[] } }
    const [loadingDetails, setLoadingDetails] = useState(false);

    const toggleDetails = async (colis) => {
        // replie si déjà ouvert
        if (expandedId === colis.id) {
            setExpandedId(null);
            return;
        }
        setExpandedId(colis.id);

        // déjà chargé -> ne rien refaire
        if (details[colis.id]) return;

        try {
            setLoadingDetails(true);
            const [artSnap, lotSnap] = await Promise.all([
                getDocs(collection(db, "colischine", colis.id, "articles")),
                getDocs(collection(db, "colischine", colis.id, "lots")),
            ]);
            const articles = artSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
            const lots = lotSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

            setDetails((prev) => ({ ...prev, [colis.id]: { articles, lots } }));
        } finally {
            setLoadingDetails(false);
        }
    };
    // ====== RENDER ======
    return (
        <div className="container py-4">
            <h3 className="mb-3">📦 Enregistrement des colis (Chine → Lomé)</h3>

            <div className="row g-3">
                {/* --- FORMULAIRE NOUVEAU COLIS --- */}
                <div className="col-lg-7">
                    <div className="card shadow-sm">
                        <div className="card-body">
                            <h5 className="card-title">Nouveau colis</h5>

                            <div className="row g-2">
                                <div className="col-md-4">
                                    <label className="form-label">Code suivi</label>
                                    <input
                                        className="form-control"
                                        value={code}
                                        onChange={(e) => setCode(e.target.value)}
                                    />
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label">Client</label>
                                    <input
                                        className="form-control"
                                        value={clientName}
                                        onChange={(e) => setClientName(e.target.value)}
                                        placeholder="Nom et prénom"
                                    />
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label">Téléphone</label>
                                    <input
                                        className="form-control"
                                        value={clientPhone}
                                        onChange={(e) => setClientPhone(e.target.value)}
                                        placeholder="Ex: +228…"
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label">Origine</label>
                                    <input
                                        className="form-control"
                                        value={origin}
                                        onChange={(e) => setOrigin(e.target.value)}
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label">Destination</label>
                                    <input
                                        className="form-control"
                                        value={destination}
                                        onChange={(e) => setDestination(e.target.value)}
                                    />
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label">Estimation (XOF)</label>
                                    <input
                                        className="form-control"
                                        value={estimate}
                                        onChange={(e) => setEstimate(e.target.value)}
                                    />
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label">Image de couverture</label>
                                    <UploadField pathPrefix={`colis/covers`} onUploaded={setCover} />
                                </div>

                                <div className="col-md-4 d-flex align-items-end">
                                    {cover && (
                                        <img
                                            src={cover}
                                            alt="cover"
                                            className="rounded border"
                                            style={{ width: 88, height: 88, objectFit: "cover" }}
                                        />
                                    )}
                                </div>

                                <div className="col-12">
                                    <ArticlesEditor articles={articles} setArticles={setArticles} />
                                </div>

                                <div className="col-12">
                                    <PaymentsEditor payments={payments} setPayments={setPayments} />
                                </div>

                                <div className="col-12">
                                    <LotsEditor lots={lots} setLots={setLots} />
                                </div>

                                {/* --- Contrôle avant enregistrement --- */}
                                <div className="col-12">
                                    <ProgressChecklist
                                        cov={coverage}
                                        paidTotalXOF={paidTotalXOF}
                                        estimateXOF={estimate}
                                        payStatus={paymentStatus}
                                        issues={coverageIssues}
                                    />
                                </div>

                                <div className="col-12">
                                    <div className="alert alert-info d-flex justify-content-between align-items-center">
                                        <div>
                                            <div>
                                                Statut: <strong>{status}</strong>
                                            </div>
                                            <div>
                                                Valeur déclarée articles :{" "}
                                                <strong>{Number(articlesTotalXOF || 0).toLocaleString("fr-FR")} XOF</strong>
                                            </div>
                                            <div>
                                                Payé:{" "}
                                                <strong>{paidTotalXOF.toLocaleString("fr-FR")} XOF</strong> — Reste:{" "}
                                                <strong>{balance.toLocaleString("fr-FR")} XOF</strong>
                                            </div>
                                        </div>
                                        <button
                                            className="btn btn-primary"
                                            type="button"
                                            // Pour bloquer si incohérences: disabled={saving || coverageIssues.length > 0}
                                            disabled={saving}
                                            onClick={save}
                                        >
                                            {saving ? "Enregistrement…" : "Enregistrer le colis"}
                                        </button>
                                    </div>
                                    {coverageIssues.length > 0 && (
                                        <div className="alert alert-warning mt-2 mb-0">
                                            <div className="fw-bold mb-1">Vérifications à corriger :</div>
                                            <ul className="mb-0">
                                                {coverageIssues.map((msg, i) => (
                                                    <li key={i}>{msg}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* --- LISTE & ACTIONS RAPIDES --- */}
                <div className="col-lg-5">
                    <div className="card shadow-sm">
                        <div className="card-body">
                            <h5 className="card-title">Colis enregistrés</h5>
                            <div className="list-group">
                                {list.map((c) => {
                                    const fullyPaid = Number(c.paidTotalXOF || 0) >= Number(c.estimateXOF || 0);
                                    const isOpen = expandedId === c.id;
                                    const det = details[c.id];

                                    // petits cumuls pour les lots si dispo
                                    const wh = (det?.lots || []).filter(l => l.step === "warehouse")
                                        .reduce((s, l) => s + Number(l.qty || 0), 0);
                                    const cont = (det?.lots || []).filter(l => l.step === "container")
                                        .reduce((s, l) => s + Number(l.qty || 0), 0);
                                    const lome = (det?.lots || []).filter(l => l.step === "lome")
                                        .reduce((s, l) => s + Number(l.qty || 0), 0);

                                    return (
                                        <div key={c.id} className="list-group-item">
                                            {/* EN-TÊTE CLIQUABLE */}
                                            <div
                                                className="d-flex gap-3 align-items-center"
                                                onClick={() => toggleDetails(c)}
                                                style={{ cursor: "pointer" }}
                                            >
                                                <img
                                                    src={c.cover || "https://via.placeholder.com/64x64?text=Colis"}
                                                    alt="c"
                                                    width={64}
                                                    height={64}
                                                    className="rounded border"
                                                    style={{ objectFit: "cover" }}
                                                />

                                                <div className="flex-grow-1">
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <div className="fw-bold">
                                                            {c.code} — {c.clientName}
                                                        </div>
                                                        <span className="small text-muted">{isOpen ? "▲" : "▼"}</span>
                                                    </div>

                                                    <div className="small text-muted">
                                                        {c.origin} → {c.destination} • Statut: {c.status}
                                                    </div>
                                                    <div className="small">
                                                        Payé: {Number(c.paidTotalXOF || 0).toLocaleString("fr-FR")} /{" "}
                                                        {Number(c.estimateXOF || 0).toLocaleString("fr-FR")} XOF
                                                    </div>

                                                    {/* ACTIONS — empêcher la propagation du clic */}
                                                    {/* ACTIONS — empêcher la propagation du clic */}
                                                    <div className="mt-2 d-flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
                                                        {!fullyPaid && (
                                                            <button
                                                                className="btn btn-sm btn-outline-primary"
                                                                onClick={() => openPayModal(c)}
                                                            >
                                                                + Paiement
                                                            </button>
                                                        )}

                                                        <button
                                                            className="btn btn-sm btn-outline-secondary"
                                                            onClick={() => openLotModal(c)}
                                                        >
                                                            + Étape (lot)
                                                        </button>

                                                        {c.status !== "recu" && (
                                                            <button
                                                                className="btn btn-sm btn-success"
                                                                disabled={!fullyPaid}
                                                                onClick={() => fullyPaid && markReceived(c.id)}
                                                                title={!fullyPaid ? "Le colis doit être totalement payé" : ""}
                                                            >
                                                                Marquer « Reçu »
                                                            </button>
                                                        )}

                                                        {/* 🗑️ Bouton supprimer (admin) */}
                                                        <button
                                                            className="btn btn-sm btn-outline-danger ms-auto"
                                                            onClick={() => deleteColis(c)}
                                                            disabled={deletingId === c.id}
                                                            title="Supprimer le colis"
                                                        >
                                                            {deletingId === c.id ? "Suppression…" : (
                                                                <>
                                                                    <TrashIcon /> <span className="ms-1">Supprimer</span>
                                                                </>
                                                            )}
                                                        </button>
                                                    </div>

                                                </div>
                                            </div>

                                            {/* DÉTAILS DÉROULANTS */}
                                            {isOpen && (
                                                <div className="mt-3">
                                                    {loadingDetails && !det && (
                                                        <div className="text-muted small">Chargement des détails…</div>
                                                    )}

                                                    {det && (
                                                        <>
                                                            {/* Tableau des articles */}
                                                            <div className="table-responsive">
                                                                <table className="table table-sm align-middle">
                                                                    <thead>
                                                                        <tr>
                                                                            <th>Article</th>
                                                                            <th className="text-end">Qté</th>
                                                                            <th className="text-end">Poids/Taille</th>
                                                                            <th className="text-end">Valeur unitaire (XOF)</th>
                                                                            <th className="text-end">Valeur ligne (XOF)</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {det.articles?.map((a) => {
                                                                            const unit = Number(a.unitValueXOF ?? a.value ?? 0);
                                                                            const qty = Number(a.qty || 0);
                                                                            const line = qty * unit;
                                                                            return (
                                                                                <tr key={a.id}>
                                                                                    <td>
                                                                                        <div className="d-flex align-items-center gap-2">
                                                                                            {Array.isArray(a.images) && a.images[0] && (
                                                                                                <img
                                                                                                    src={a.images[0]}
                                                                                                    alt=""
                                                                                                    width={36}
                                                                                                    height={36}
                                                                                                    className="rounded border"
                                                                                                    style={{ objectFit: "cover" }}
                                                                                                />
                                                                                            )}
                                                                                            <span>{a.title || "-"}</span>
                                                                                        </div>
                                                                                    </td>
                                                                                    <td className="text-end">{qty}</td>
                                                                                    <td className="text-end">{a.weight || "-"}kg</td>
                                                                                    <td className="text-end">
                                                                                        {unit.toLocaleString("fr-FR")}
                                                                                    </td>
                                                                                    <td className="text-end fw-semibold">
                                                                                        {line.toLocaleString("fr-FR")}
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        })}
                                                                        {(!det.articles || det.articles.length === 0) && (
                                                                            <tr>
                                                                                <td colSpan={5} className="text-center text-muted">
                                                                                    Aucun article enregistré.
                                                                                </td>
                                                                            </tr>
                                                                        )}
                                                                    </tbody>
                                                                </table>
                                                            </div>

                                                            {/* Résumé des étapes */}
                                                            <div className="small">
                                                                <span className="me-3">Étapes :</span>
                                                                <span className="me-3">Entrepôt (Chine) : <strong>{wh}</strong></span>
                                                                <span className="me-3">Conteneur : <strong>{cont}</strong></span>
                                                                <span>Réception à Lomé : <strong>{lome}</strong></span>
                                                            </div>
                                                        </>
                                                    )}
                                                    {/* Liens & numéros des conteneurs (si présents) */}
                                                    {(det?.lots || []).some((l) => l.step === "container" && (l.containerNo || l.shippingUrl)) && (
                                                        <div className="mt-2 small">
                                                            <div className="fw-semibold mb-1">Conteneurs :</div>
                                                            <ul className="mb-0">
                                                                {(det.lots || [])
                                                                    .filter((l) => l.step === "container" && (l.containerNo || l.shippingUrl))
                                                                    .map((l) => (
                                                                        <li key={l.id}>
                                                                            {l.containerNo && <span className="me-2">N° <strong>{l.containerNo}</strong></span>}
                                                                            {l.shippingUrl && (
                                                                                <a href={l.shippingUrl} target="_blank" rel="noopener noreferrer">
                                                                                    Ouvrir le site de shipping
                                                                                </a>
                                                                            )}
                                                                            {(!l.containerNo && !l.shippingUrl) && <span className="text-muted">—</span>}
                                                                        </li>
                                                                    ))}
                                                            </ul>
                                                        </div>
                                                    )}

                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                {list.length === 0 && <div className="text-muted">Aucun colis encore.</div>}
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            {/* ======= MODALE PAIEMENT ======= */}
            {showPay && (
                <div className="modal fade show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,.5)" }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Ajouter un paiement — {activeColis?.code}</h5>
                                <button type="button" className="btn-close" onClick={() => setShowPay(false)} />
                            </div>
                            <div className="modal-body">
                                <div className="row g-2">
                                    <div className="col-6">
                                        <label className="form-label">Montant</label>
                                        <input
                                            className="form-control"
                                            value={payForm.amount}
                                            onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
                                        />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label">Devise</label>
                                        <select
                                            className="form-select"
                                            value={payForm.currency}
                                            onChange={(e) => setPayForm({ ...payForm, currency: e.target.value })}
                                        >
                                            <option>XOF</option>
                                            <option>USD</option>
                                            <option>CNY</option>
                                            <option>EUR</option>
                                        </select>
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label">Taux</label>
                                        <input
                                            className="form-control"
                                            value={payForm.rate}
                                            onChange={(e) => setPayForm({ ...payForm, rate: e.target.value })}
                                        />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label">Méthode</label>
                                        <select
                                            className="form-select"
                                            value={payForm.method}
                                            onChange={(e) => setPayForm({ ...payForm, method: e.target.value })}
                                        >
                                            <option value="cash">Cash</option>
                                            <option value="mobile_money">Mobile Money</option>
                                            <option value="bank">Virement</option>
                                            <option value="wechat">WeChat</option>
                                            <option value="alipay">Alipay</option>
                                        </select>
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label">Note</label>
                                        <input
                                            className="form-control"
                                            value={payForm.note}
                                            onChange={(e) => setPayForm({ ...payForm, note: e.target.value })}
                                            placeholder="Réf, tranche 2, etc."
                                        />
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label">Date</label>
                                        <input
                                            type="datetime-local"
                                            className="form-control"
                                            value={payForm.date?.slice(0, 16)}
                                            onChange={(e) =>
                                                setPayForm({ ...payForm, date: new Date(e.target.value).toISOString() })
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowPay(false)}>
                                    Annuler
                                </button>
                                <button className="btn btn-primary" onClick={submitPayment}>
                                    Enregistrer le paiement
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ======= MODALE ÉTAPE (LOT) ======= */}
            {/* ======= MODALE ÉTAPE (LOT) ======= */}
            {showLot && (
                <div className="modal fade show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,.5)" }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Ajouter une étape (lot) — {activeColis?.code}</h5>
                                <button type="button" className="btn-close" onClick={() => setShowLot(false)} />
                            </div>
                            <div className="modal-body">
                                <div className="row g-2">
                                    <div className="col-md-4">
                                        <label className="form-label">Étape</label>
                                        <select
                                            className="form-select"
                                            value={lotForm.step}
                                            onChange={(e) => setLotForm({ ...lotForm, step: e.target.value })}
                                        >
                                            <option value="warehouse">Entrepôt (Chine)</option>
                                            <option value="container">Mise en conteneur</option>
                                            <option value="lome">Réception à Lomé</option>
                                        </select>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">Quantité</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={lotForm.qty}
                                            onChange={(e) => setLotForm({ ...lotForm, qty: Number(e.target.value || 0) })}
                                        />
                                    </div>
                                    <div className="col-md-5">
                                        <label className="form-label">Note</label>
                                        <input
                                            className="form-control"
                                            value={lotForm.note}
                                            onChange={(e) => setLotForm({ ...lotForm, note: e.target.value })}
                                            placeholder="Ex: 2 cartons déposés à l'entrepôt"
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">Date</label>
                                        <input
                                            type="datetime-local"
                                            className="form-control"
                                            value={lotForm.date?.slice(0, 16)}
                                            onChange={(e) =>
                                                setLotForm({ ...lotForm, date: new Date(e.target.value).toISOString() })
                                            }
                                        />
                                    </div>

                                    {/* 👇 Champs spécifiques à 'container' */}
                                    {lotForm.step === "container" && (
                                        <>
                                            <div className="col-md-6">
                                                <label className="form-label">N° de conteneur</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Ex: MSCU1234567"
                                                    value={lotForm.containerNo}
                                                    onChange={(e) => setLotForm({ ...lotForm, containerNo: e.target.value })}
                                                />
                                            </div>
                                            <div className="col-md-12">
                                                <label className="form-label">Lien du site de shipping</label>
                                                <input
                                                    type="url"
                                                    className="form-control"
                                                    placeholder="https://..."
                                                    value={lotForm.shippingUrl}
                                                    onChange={(e) => setLotForm({ ...lotForm, shippingUrl: e.target.value })}
                                                />
                                            </div>
                                        </>
                                    )}

                                    <div className="col-md-12">
                                        <label className="form-label">Preuves (images)</label>
                                        <UploadField
                                            pathPrefix="colis/lots"
                                            onUploaded={(url) =>
                                                setLotForm((f) => ({ ...f, images: [...(f.images || []), url] }))
                                            }
                                        />
                                        {(lotForm.images || []).length > 0 && (
                                            <div className="d-flex flex-wrap gap-2 mt-2">
                                                {lotForm.images.map((u, idx) => (
                                                    <div key={idx} className="position-relative">
                                                        <img
                                                            src={u}
                                                            alt="lot"
                                                            style={{ width: 72, height: 72, objectFit: "cover" }}
                                                            className="rounded border"
                                                        />
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-light position-absolute top-0 end-0"
                                                            onClick={() =>
                                                                setLotForm((f) => ({
                                                                    ...f,
                                                                    images: f.images.filter((_, k) => k !== idx),
                                                                }))
                                                            }
                                                        >
                                                            ×
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowLot(false)}>
                                    Annuler
                                </button>
                                <button className="btn btn-primary" onClick={submitLot}>
                                    Enregistrer l’étape
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}
